import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { brandColors, getGradeColor } from '@config/brandTokens';
import type { ContestGradeCode } from '../../types/contestCatalog';

export interface ContestCompareSkillRow {
  title: string;
  selfValue: string;
  selfGrade: ContestGradeCode | null;
  selfDate: string;
  contestValue: string;
  contestGrade: ContestGradeCode | null;
  contestRecordedDate: string;
  contestSubmittedDate: string;
  contestStatus: string;
  contestSelfCounts?: string;
}

function GradeChip({ grade }: { grade: ContestGradeCode | null }) {
  if (!grade) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  const colors = getGradeColor(grade);
  return (
    <Chip
      size="small"
      label={grade}
      sx={{
        fontWeight: 900,
        height: 22,
        minWidth: 28,
        bgcolor: colors.bg,
        color: colors.text,
        border: `1px solid ${colors.border}`,
      }}
    />
  );
}

function dash(value: string): string {
  return String(value || '').trim() || '—';
}

export default function ContestResultsCompareDialog({
  open,
  onClose,
  playerName,
  ageCategory,
  rows,
}: {
  open: boolean;
  onClose: () => void;
  playerName?: string;
  ageCategory: string;
  rows: ContestCompareSkillRow[];
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 800, pr: 6, color: brandColors.navy, fontFamily: '"Sora", Arial, sans-serif' }}>
        Self Evaluation vs Contest
        <Typography component="span" sx={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: brandColors.slate, mt: 0.35 }}>
          {playerName ? `${playerName} · ` : ''}
          {ageCategory}
        </Typography>
      </DialogTitle>
      <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ bgcolor: brandColors.ice, p: 0 }}>
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" rowSpan={2} sx={headCellSx}>
                Skill
              </Box>
              <Box component="th" colSpan={3} sx={{ ...headCellSx, bgcolor: '#efe6f8' }}>
                Self Evaluation
              </Box>
              <Box component="th" colSpan={5} sx={{ ...headCellSx, bgcolor: '#fde8dc' }}>
                Contest
              </Box>
            </Box>
            <Box component="tr">
              <Box component="th" sx={{ ...headCellSx, bgcolor: '#efe6f8' }}>Score</Box>
              <Box component="th" sx={{ ...headCellSx, bgcolor: '#efe6f8' }}>Grade</Box>
              <Box component="th" sx={{ ...headCellSx, bgcolor: '#efe6f8' }}>Date</Box>
              <Box component="th" sx={{ ...headCellSx, bgcolor: '#fde8dc' }}>Score</Box>
              <Box component="th" sx={{ ...headCellSx, bgcolor: '#fde8dc' }}>Grade</Box>
              <Box component="th" sx={{ ...headCellSx, bgcolor: '#fde8dc' }}>Self counts</Box>
              <Box component="th" sx={{ ...headCellSx, bgcolor: '#fde8dc' }}>Recorded</Box>
              <Box component="th" sx={{ ...headCellSx, bgcolor: '#fde8dc' }}>Submitted</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {rows.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={9} sx={{ ...bodyCellSx, textAlign: 'center', color: brandColors.slate, py: 3 }}>
                  No skills to compare for this age yet.
                </Box>
              </Box>
            ) : (
              rows.map((row) => (
                <Box component="tr" key={row.title}>
                  <Box component="td" sx={{ ...bodyCellSx, fontWeight: 700, color: brandColors.navy, textAlign: 'left' }}>
                    {row.title}
                  </Box>
                  <Box component="td" sx={bodyCellSx}>{dash(row.selfValue)}</Box>
                  <Box component="td" sx={bodyCellSx}><GradeChip grade={row.selfGrade} /></Box>
                  <Box component="td" sx={bodyCellSx}>{dash(row.selfDate)}</Box>
                  <Box component="td" sx={bodyCellSx}>
                    {dash(row.contestValue)}
                    {row.contestStatus && row.contestStatus !== 'submitted' && !row.contestValue ? (
                      <Typography component="span" variant="caption" sx={{ display: 'block', color: brandColors.slate }}>
                        {row.contestStatus.replace('_', ' ')}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box component="td" sx={bodyCellSx}><GradeChip grade={row.contestGrade} /></Box>
                  <Box component="td" sx={bodyCellSx}>{dash(row.contestSelfCounts || '')}</Box>
                  <Box component="td" sx={bodyCellSx}>{dash(row.contestRecordedDate)}</Box>
                  <Box component="td" sx={bodyCellSx}>{dash(row.contestSubmittedDate)}</Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const headCellSx = {
  px: 1,
  py: 0.85,
  fontSize: '0.68rem',
  fontWeight: 800,
  letterSpacing: 0.4,
  textTransform: 'uppercase' as const,
  color: brandColors.navy,
  borderBottom: '1px solid #e2e8f0',
  textAlign: 'center' as const,
};

const bodyCellSx = {
  px: 1,
  py: 0.9,
  borderBottom: '1px solid #eef2f6',
  textAlign: 'center' as const,
  bgcolor: brandColors.white,
};
